import React, { createContext, useContext, useState, useCallback } from 'react';
import Modal from '../components/Modal';

interface ConfirmOptions {
	title: string;
	content: string;
	confirmText?: string;
	cancelText?: string;
	confirmColor?: string;
	hideCancel?: boolean;
}

interface ModalContextType {
	confirm: (options: ConfirmOptions) => Promise<boolean>;
	showAlert: (title: string, content: string) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<ConfirmOptions>({ title: '', content: '' });
	const [resolve, setResolve] = useState<((value: boolean) => void) | null>(null);

	const confirm = useCallback((opts: ConfirmOptions) => {
		setOptions(opts);
		setOpen(true);
		return new Promise<boolean>((res) => {
			setResolve(() => res);
		});
	}, []);

	const showAlert = useCallback((title: string, content: string) => {
		return confirm({ title, content, hideCancel: true, confirmText: 'OK' });
	}, [confirm]);

	const handleClose = useCallback(() => {
		setOpen(false);
		if (resolve) resolve(false);
	}, [resolve]);

	const handleConfirm = useCallback(() => {
		setOpen(false);
		if (resolve) resolve(true);
	}, [resolve]);

	return (
		<ModalContext.Provider value={{ confirm, showAlert }}>
			{children}
			<Modal
				isOpen={open}
				onClose={handleClose}
				title={options.title}
				actions={
					<>
						{!options.hideCancel && (
							<button
								onClick={handleClose}
								style={{
									background: 'transparent',
									border: '1px solid rgba(255,255,255,0.2)',
									color: '#fff',
									padding: '10px 20px',
									borderRadius: '8px',
									cursor: 'pointer'
								}}
							>
								{options.cancelText || 'Cancel'}
							</button>
						)}
						<button
							onClick={handleConfirm}
							style={{
								background: options.confirmColor || '#EFA43B',
								border: 'none',
								color: '#fff',
								padding: '10px 20px',
								borderRadius: '8px',
								fontWeight: 'bold',
								cursor: 'pointer'
							}}
						>
							{options.confirmText || 'Confirm'}
						</button>
					</>
				}
			>
				<p style={{ margin: 0, whiteSpace: 'pre-line' }}>{options.content}</p>
			</Modal>
		</ModalContext.Provider>
	);
};

export const useModal = () => {
	const context = useContext(ModalContext);
	if (!context) {
		throw new Error('useModal must be used within a ModalProvider');
	}
	return context;
};
